import { useEffect, useRef, useCallback } from 'react';
import { driver } from 'driver.js';

const STORAGE_PREFIX = 'tour_';
const STORAGE_SUFFIX = '_v2_done';

function isHighlightable(el) {
  if (!el) return false;
  if (typeof el.checkVisibility === 'function') {
    return el.checkVisibility({ checkOpacity: false });
  }
  let n = el;
  while (n && n.nodeType === 1) {
    const s = window.getComputedStyle(n);
    if (s.display === 'none' || s.visibility === 'hidden') return false;
    n = n.parentElement;
  }
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

function visibleSteps(steps) {
  if (typeof document === 'undefined') return steps || [];
  return (steps || []).filter((s) => {
    if (!s?.element) return true;
    try {
      const el = document.querySelector(s.element);
      return isHighlightable(el);
    } catch {
      return false;
    }
  });
}

export function useProductTour(
  tourId,
  steps,
  { autoStart = true, delay = 1200, listenGlobal = true } = {},
) {
  const driverRef = useRef(null);
  const timerRef = useRef(null);
  const startedRef = useRef(false);

  const storageKey = `${STORAGE_PREFIX}${tourId}${STORAGE_SUFFIX}`;

  const createDriver = useCallback((filtered) => {
    if (driverRef.current) {
      driverRef.current.destroy();
    }
    startedRef.current = false;

    driverRef.current = driver({
      showProgress: true,
      animate: true,
      allowClose: true,
      overlayColor: 'rgba(0, 0, 0, 0.55)',
      stagePadding: 8,
      stageRadius: 8,
      popoverClass: 'cadencya-tour-popover',
      nextBtnText: 'Siguiente',
      prevBtnText: 'Anterior',
      doneBtnText: 'Entendido',
      progressText: '{{current}} de {{total}}',
      onHighlightStarted: () => {
        startedRef.current = true;
      },
      onDestroyStarted: () => {
        if (startedRef.current) {
          localStorage.setItem(storageKey, 'true');
        }
        driverRef.current?.destroy();
      },
      steps: filtered,
    });

    return driverRef.current;
  }, [storageKey]);

  const startTour = useCallback(() => {
    const filtered = visibleSteps(steps);
    if (filtered.length === 0) return;
    const d = createDriver(filtered);
    d.drive();
  }, [createDriver, steps]);

  useEffect(() => {
    if (!autoStart) return;

    const alreadySeen = localStorage.getItem(storageKey);
    if (alreadySeen) return;

    timerRef.current = setTimeout(() => {
      startTour();
    }, delay);

    return () => {
      clearTimeout(timerRef.current);
    };
  }, [autoStart, storageKey, delay, startTour]);

  useEffect(() => {
    if (!listenGlobal) return;
    const handler = () => startTour();
    window.addEventListener('start-product-tour', handler);
    return () => window.removeEventListener('start-product-tour', handler);
  }, [listenGlobal, startTour]);

  useEffect(() => {
    return () => {
      driverRef.current?.destroy();
    };
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem(storageKey);
  }, [storageKey]);

  return { startTour, resetTour };
}
